import React from "react";
import { Box, Paper, Stack } from "@mui/material";
import type { Swagpack } from "../../../../models/Swagstore/Packs";
import CustomTypography from "../../common/form/CustomTypography";
import { getPackDisplayImage } from "../../../../utils/packImageUtils";
import AppButton from "../../common/buttons/AppButton";

interface CatalogueProductGridProps {
  products: Swagpack[];
  onGenerateProduct: (product: Swagpack) => void;
  onViewProduct: (product: Swagpack) => void;
  statusTab?: "all" | "pending" | "changes_requested" | "approved" | "not_uploaded" | "deleted";
  disableView?: boolean;
}

const CatalogueProductGrid: React.FC<CatalogueProductGridProps> = ({
  products,
  onGenerateProduct,
  onViewProduct,
  statusTab = "all",
  disableView = false,
}) => {
  const showMockupBadge = statusTab === "all" || statusTab === "pending";
  const getMockupBadgeLabel = (product: Swagpack): string => {
    const uploadedCount = Math.max(0, Number(product.mockups_uploaded_count ?? 0));
    const pendingCount = Math.max(0, Number(product.mockups_pending_count ?? 0));
    const approvedCount = Math.max(0, uploadedCount - pendingCount);
    if (uploadedCount <= 0) return "Not Uploaded";
    if (pendingCount <= 0) return `Approved ${uploadedCount}/${uploadedCount}`;
    if (pendingCount >= uploadedCount) {
      return `Pending ${pendingCount}/${uploadedCount}`;
    }
    return `Approved ${approvedCount}/${uploadedCount}`;
  };

  const getMockupBadgeStyles = (product: Swagpack) => {
    const uploadedCount = Math.max(0, Number(product.mockups_uploaded_count ?? 0));
    const pendingCount = Math.max(0, Number(product.mockups_pending_count ?? 0));
    if (uploadedCount > 0 && pendingCount <= 0) {
      return {
        color: "success.dark",
        backgroundColor: "rgba(34, 197, 94, 0.12)",
        borderColor: "rgba(34, 197, 94, 0.35)",
      };
    }
    return {
      color: "warning.dark",
      backgroundColor: "rgba(245, 158, 11, 0.14)",
      borderColor: "rgba(245, 158, 11, 0.35)",
    };
  };

  const isViewDisabledForProduct = (product: Swagpack): boolean => {
    if (disableView) return true;
    if (statusTab === "all") {
      const uploadedCount = Math.max(0, Number(product.mockups_uploaded_count ?? 0));
      return uploadedCount <= 0;
    }
    return false;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        alignItems: "stretch",
        gridTemplateColumns: "minmax(0, 1fr)",
        "@media (min-width: 700px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        },
        "@media (min-width: 1200px)": {
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        },
        "@media (min-width: 1600px)": {
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        },
      }}
    >
      {products?.map((product) => (
        <Box
          key={product.id}
          sx={{
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 2.5,
              borderColor: "rgba(15, 23, 42, 0.08)",
              height: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, flex: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "1 / 1",
                  maxHeight: { xs: 120, sm: 160, md: 180 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  bgcolor: "rgba(15, 23, 42, 0.03)",
                  pt: showMockupBadge ? 2.25 : 0,
                }}
              >
                {showMockupBadge && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      borderRadius: "999px",
                      px: 1,
                      py: 0.35,
                      border: "1px solid",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      ...getMockupBadgeStyles(product),
                    }}
                  >
                    {getMockupBadgeLabel(product)}
                  </Box>
                )}
                <Box
                  component="img"
                  src={getPackDisplayImage(product)}
                  alt={product.label ?? "Product"}
                  sx={{
                    mt: showMockupBadge ? 2.25 : 0,
                    width: "88%",
                    height: "88%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <CustomTypography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    minHeight: "2.4em",
                    mt: 0.25,
                  }}
                >
                  {product.label ?? "Product"}
                </CustomTypography>
                <CustomTypography variant="caption" color="text.secondary">
                  ID: {product.id}
                </CustomTypography>
              </Box>
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 1,
                width: "100%",
                minWidth: 0,
                "& > *": { flex: 1, minWidth: 0 },
              }}
            >
              <AppButton
                label="Generate"
                variant="primary"
                onClick={() => onGenerateProduct(product)}
                customStyles={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: "32px",
                  padding: "4px 8px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              />
              <AppButton
                label="View"
                variant="secondary"
                onClick={() => onViewProduct(product)}
                disabled={isViewDisabledForProduct(product)}
                customStyles={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: "32px",
                  padding: "4px 8px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              />
            </Stack>
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

export default CatalogueProductGrid;
